import { AfterViewInit, Component, DoCheck, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { BuildingBlock, BuildingBlockControlBaseComponent } from '../building-block.types';
import { AssetAllocation } from './asset-allocation.models';
import { InputStatus } from '@workflow/directive/input-mask/input-mask-processing.base';
import { maskInput } from '@workflow/directive/input-mask/mask.helper';
import { getValidationObject } from '@workflow/helper/validation-converter/validation-converter';

const MAX_PERCENT = 100;
const BeneficiaryType = { primary: 'Primary', contingent: 'Contingent' };
const BENEFICIARY = 'api:oaoMemberInformation/beneficiary';
const ALLOCATIONSTGID = 'api:oaoMemberInformation/beneficiary/$/allocationPercent';
const BENEFICIARYTYPESTGID = 'api:oaoMemberInformation/beneficiary/$/allocationType';

/** For beneficiary asset allocation */
@BuildingBlock('asset-allocation')
@Component({
  selector: 'ecp-asset-allocation',
  templateUrl: './asset-allocation.component.html'
})
export class AssetAllocationComponent extends BuildingBlockControlBaseComponent implements OnInit, DoCheck, AfterViewInit {
  public get allocationLeft(): number {
    return parseFloat((MAX_PERCENT - this.totalAllocation).toFixed(0));
  }
  public get allocationMore(): number {
    return parseFloat((this.totalAllocation - MAX_PERCENT).toFixed(0));
  }

  /** form value */
  @ViewChild('sharedCertForm') public form: NgForm;
  /** list of beneficiary */
  public beneficiaryList: AssetAllocation[] = [];
  /** previous Array size */
  public previousSize = 0;
  /** required */
  public validation = ['required'];
  /** allocation validation error */
  public allocationValidation = [];
  /** boolean flag for false allocation */
  public falseAllocation = false;
  /** Error Message */
  public errorMessage = {
    v1: 'Sum of each beneficiary type should have 100% allocation',
    v2: '% left to allocate:',
    v3: '% over allocation'
  };
  /** last state of mobile number input */
  private lastState: InputStatus;

  /** Total Allocation */
  public totalAllocation = 0;
  public totalBeneficiaries = 0;

  public ngDoCheck(): void {
    this.addItem();
  }

  /** OnInit */
  public ngOnInit(): void {
    this.addItem();
    this.statefulService.wrap('mem:refresh').afterPatch(() => {
      this.setItemValues();
    });
    this.validateAllocation();
  }

  // tslint:disable-next-line: completed-docs
  public onChange(event, item, iterationId): void {
    this.statefulService.wrap(item.storageId, iterationId).value = event.target.value;
    this.validateAllocation();
  }

  ngAfterViewInit(): void {
    this.validateAllocation();
  }

  public checkCardType(fieldId, allocationType): boolean {
    switch (fieldId) {
      case 'oao-beneficiary-primary-allocation':
        return allocationType === BeneficiaryType.primary;
      case 'oao-beneficiary-contingent-allocation':
        return allocationType === BeneficiaryType.contingent;
    }
  }

  /**
   * addItem
   */
  public addItem(): void {
    const allSize = this.statefulService.arraySize(BENEFICIARY);
    let currentSize = 0;
    for (let i = 0; i < allSize; i++) {
      const allocationType = this.statefulService.wrap(BENEFICIARYTYPESTGID, i).valueStringOnly;
      if (this.checkCardType(this.field.id, allocationType)) {
        currentSize += 1;
      }
    }
    if (currentSize !== this.previousSize || allSize !== this.totalBeneficiaries) {
      this.setItemValues();
      this.previousSize = currentSize;
      this.totalBeneficiaries = allSize;
    }
  }

  private setItemValues(): void {
    const allSize = this.statefulService.arraySize(BENEFICIARY);

    this.beneficiaryList = [];
    for (let i = 0; i < allSize; i++) {
      const allocationType = this.statefulService.wrap(BENEFICIARYTYPESTGID, i).valueStringOnly;
      if (this.checkCardType(this.field.id, allocationType)) {
        this.beneficiaryList.push({
          acutalIndex: i,
          allocation: {
            storageId: ALLOCATIONSTGID,
            value: this.statefulService.wrap(ALLOCATIONSTGID, i).valueStringOnly
          },
          type: {
            storageId: BENEFICIARYTYPESTGID,
            value: this.statefulService.wrap(BENEFICIARYTYPESTGID, i).valueStringOnly
          }
        });
      }
    }
    this.validateAllocation();
  }

  /** methods helps to process mask */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public processMask(inputElement: any, item, setSelection = true): void {
    const mask = maskInput('percent', this.readState(inputElement), this.lastState, getValidationObject(this.field));
    if (mask.changed) {
      inputElement.target.value = mask.processed.value;
      if (setSelection && inputElement.target.value) {
        inputElement.target.setSelectionRange(mask.processed.selection[0], mask.processed.selection[1]);
      }
      item.value = this.removeZeros(mask.processed.value);
    }
    this.lastState = this.readState(inputElement);
  }

  /** Read inputelement state */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readState(inputElement: any): InputStatus {
    const domElement = inputElement.target;
    return {
      focused: this.focused,
      selection: [domElement.selectionStart, domElement.selectionEnd],
      value: inputElement.target.value
    };
  }
  public validateAsset(): boolean {
    this.totalAllocation = 0;
    this.allocationValidation = [];
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.beneficiaryList.forEach((t, j) => {
      const val = this.statefulService.wrap(t.allocation.storageId, t.acutalIndex).valueNumberOnly;
      this.totalAllocation += val ? val : 0;
      this.totalAllocation = parseFloat(this.totalAllocation.toFixed(0));
    });

    if (this.totalAllocation !== MAX_PERCENT) {
      return true;
    }
    return false;
  }
  /** Validate Allocation Details */
  public validateAllocation(): void {
    this.falseAllocation = this.validateAsset();
    this.field.data.value = this.form && this.form.valid && !this.falseAllocation ? 'true' : '';
  }

  private removeZeros(value: string): string {
    return value && value.length > 1 && value[0] === '0' ? value.substring(1) : value;
  }
}
